import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Snack } from '@ilteducation/videos';
import type { Category } from '@ilteducation/content-ref-data-types';
import { useTheme } from 'vuetify';

/** Persisted to localStorage under the `gui` key. Every field is absent until the user sets it. */
export interface GuiSettings {
  activeAudioLanguage?: string;
  activeSubtitleLanguage?: string;
  skipVideoIntros?: boolean;
  videoVolume?: string;
}

const useGuiStore = defineStore('gui', () => {
  const snack = ref<Snack>({
    message: '',
    color: '',
    show: false,
  });
  const isBurgerMenuOpen = ref(false);
  const settings = ref<GuiSettings>(JSON.parse(window.localStorage.getItem('gui') || '{}'));

  const toggleBurgerMenu = () => {
    isBurgerMenuOpen.value = !isBurgerMenuOpen.value;
  };
  const closeBurgerMenu = () => {
    isBurgerMenuOpen.value = false;
  };

  const theme = useTheme();

  const themeToSubjectSlugMap = {
    biology: ['biologi', 'biologia'],
    civics: ['samhallskunskap', 'samfunnsfag', 'yhteiskuntaoppi'],
    english: ['engelska', 'engelsk', 'englanti'],
    physics: ['fysik', 'fysikk', 'fysiikka'],
    history: ['historia', 'historie'],
    chemistry: ['kemi', 'kjemi', 'kemia'],
    geography: ['geografi', 'maantieto'],
    physicalEducation: ['idrott-och-halsa', 'liikuntaa-ja-terveystieto'],
    mathematics: ['matematik', 'matematikk', 'matematiikka'],
    school: ['greppa-skolan'],
    homeAndConsumerStudies: ['hem-och-konsumentkunskap'],
    childrenAndRecreation: ['barn-och-fritid'],
    marketLanguage: ['svenskasva', 'suomen-kieli'],
    technology: ['teknik'],
    healthAndSocialCare: ['vard-och-omsorg'],
    crafts: ['slojd'],
    vehiclesAndTransport: ['fordon-och-transport'],
    religion: ['religion', 'uskonto', 'krle'],
    nativeLanguage: ['modersmal'],
  };

  const getThemeName = (subject: Category['slug']) => {
    let themeName = 'light';
    if (!subject) return themeName;
    Object.entries(themeToSubjectSlugMap).forEach(([t, slugs]) => {
      if (slugs.includes(subject)) {
        themeName = t;
      }
    });
    if (theme.current.value.dark) {
      themeName = `${themeName}Dark`;
    }
    return themeName;
  };

  watch(
    settings,
    (newSettings) => {
      window.localStorage.setItem('gui', JSON.stringify(newSettings));
    },
    { deep: true },
  );

  return {
    snack,
    settings,
    isBurgerMenuOpen: computed(() => isBurgerMenuOpen.value),
    toggleBurgerMenu,
    closeBurgerMenu,
    getThemeName,
  };
});

export default useGuiStore;
